// components/WorkflowEditGuide.jsx
import { useEffect, useState } from "react";
import Joyride, { STATUS } from "react-joyride";
import { useLocation } from "react-router-dom";

export default function WorkflowEditGuide() {
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    const shouldRun =
      location.search.includes("guide=true") ||
      location.state?.fromWorkflowListGuide;

    if (!shouldRun) return;

    if (location.search.includes("guide=true")) {
      window.history.replaceState({}, "", location.pathname);
    }

    const timer = setTimeout(() => {
      const newSteps = [
        {
          target: "body",
          content: (
            <div className="text-center">
              <h5 className="fw-bold mb-3 text-primary-600">
                Welcome to Workflow Builder
              </h5>
              <p className="text-neutral-600">
                Let's set up your parking workflow in 3 simple steps. This will take less than 2 minutes!
              </p>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        {
          target: ".react-select__control",
          content: (
            <div>
              <h6 className="fw-bold text-primary-600 mb-2">
                Step 2: Select Branch & Location
              </h6>
              <p>
                First, choose the <strong>branch</strong> and{" "}
                <strong>location</strong> where this workflow will be used.
              </p>
              <small className="text-success-600">
                This filters locations & staff automatically
              </small>
            </div>
          ),
          placement: "center",
          disableBeacon: true,
        },
        {
          target: ".custom-node-action-btn.assign-staff-btn",
          content: (
            <div>
              <h6 className="fw-bold text-danger-600 mb-3">
                Step 3: Assign Staff to Every Step!
              </h6>
              <p>
                Without staff assignment,{" "}
                <strong>this step will NOT work</strong> in real operations.
              </p>
              <p className="fw-bold text-success-600 mt-3">
                Click this button on every step → Assign at least 1 staff
              </p>
            </div>
          ),
          placement: "top-end",
          disableBeacon: true,
        },
      ];

      setSteps(newSteps);
      setRun(true);
    }, 1000);

    return () => {
      clearTimeout(timer);
      document.querySelectorAll(".joyride-glow").forEach((el) => {
        el.classList.remove("joyride-glow");
        delete el.dataset.guideLabel;
      });
    };
  }, [location]);

  const handleJoyrideCallback = (data) => {
    const { status, index } = data;

    // Remove all glows first
    document.querySelectorAll(".joyride-glow").forEach((el) => {
      el.classList.remove("joyride-glow");
      delete el.dataset.guideLabel;
    });

    // Add glow based on current step
    if (index === 1) {
      // Step 2: Branch & Location
      document.querySelectorAll(".react-select__control").forEach((el, i) => {
        el.classList.add("joyride-glow");
        if (i === 0) {
          el.dataset.guideLabel = "Select Branch";
        } else if (i === 1) {
          el.dataset.guideLabel = "Then Select Location";
        }
      });
    } else if (index === 2) {
      // Step 3: Staff Assign button - Wait for React Flow nodes to render
      setTimeout(() => {
        document.querySelectorAll(".custom-node-action-btn.assign-staff-btn").forEach((el) => {
          el.classList.add("joyride-glow");
          el.dataset.guideLabel = "Assign Staff to Step";
        });
      }, 100);
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      document.querySelectorAll(".joyride-glow").forEach((el) => {
        el.classList.remove("joyride-glow");
        delete el.dataset.guideLabel;
      });
      setRun(false);
    }
  };

  if (!run) return null;

  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        scrollToFirstStep={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            zIndex: 99999,
            primaryColor: "var(--brand)",
            backgroundColor: "var(--white)",
            textColor: "var(--neutral-800)",
            arrowColor: "var(--white)",
            overlayColor: "rgba(27, 36, 49, 0.92)",
          },
          buttonNext: {
            backgroundColor: "var(--brand)",
            color: "white",
            borderRadius: "var(--rounded-16)",
            padding: "12px 32px",
            fontWeight: "600",
          },
          tooltip: {
            borderRadius: "var(--rounded-20)",
            padding: "32px",
            maxWidth: "460px",
            boxShadow: "var(--shadow-4)",
            backgroundColor: "var(--white)",
          },
        }}
        locale={{ back: "Back", next: "Next", last: "Done", skip: "Skip" }}
      />

      {/* Unified CSS - Dono Cases Ke Liye Kaam Karega */}
      <style jsx>{`
        @keyframes glowPulse {
          0%,
          100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
          }
          50% {
            box-shadow: 0 0 0 20px rgba(59, 130, 246, 0);
          }
        }
        
        /* Base Glow Effect */
        .joyride-glow {
          position: relative !important;
          z-index: 999999 !important;
          animation: glowPulse 2s infinite ease-in-out !important;
          border: 4px solid var(--brand) !important;
          border-radius: 12px !important;
        }
        
        /* Floating Label - Universal Solution */
        .joyride-glow[data-guide-label] {
          position: relative !important;
        }
        
        .joyride-glow[data-guide-label]::after {
          content: attr(data-guide-label) !important;
          position: absolute !important;
          top: -50px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          background: var(--brand) !important;
          color: white !important;
          padding: 8px 16px !important;
          border-radius: 20px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          white-space: nowrap !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
          z-index: 1000000 !important;
          pointer-events: none !important;
          min-width: max-content !important;
        }
        
        .joyride-glow[data-guide-label]::before {
          content: "" !important;
          position: absolute !important;
          top: -18px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          border: 8px solid transparent !important;
          border-top-color: var(--brand) !important;
          z-index: 1000000 !important;
          pointer-events: none !important;
        }
        
        /* Special red glow for Assign Staff button */
        .assign-staff-btn.joyride-glow {
          border-color: #ef4444 !important;
        }
        
        .assign-staff-btn.joyride-glow[data-guide-label]::after {
          background: #ef4444 !important;
        }
        
        .assign-staff-btn.joyride-glow[data-guide-label]::before {
          border-top-color: #ef4444 !important;
        }

        /* Fix for React Select elements - ensure they're visible */
        .react-select__control.joyride-glow {
          overflow: visible !important;
        }
        
        /* Fix for staff assign buttons in React Flow */
        .custom-node-action-btn.assign-staff-btn.joyride-glow {
          overflow: visible !important;
        }
          .assign-staff-btn.joyride-glow {
          position: relative !important;
          z-index: 999999 !important;
          animation: glowPulse 2s infinite ease-in-out !important;
          border: 4px solid var(--brand) !important;
          border-radius: 12px !important;
        }
        
        /* Floating Label Styles - IMPORTANT FIX */
        .assign-staff-btn.joyride-glow[data-guide-label]::after {
          content: attr(data-guide-label) !important;
          position: fixed !important;
          top: -60px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          background: var(--brand) !important;
          color: white !important;
          padding: 12px 24px !important;
          border-radius: 30px !important;
          font-size: 14px !important;
          font-weight: bold !important;
          white-space: nowrap !important;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3) !important;
          z-index: 1000000 !important;
          pointer-events: none !important;
        }
        
        .assign-staff-btn.joyride-glow[data-guide-label]::before {
          content: "" !important;
          position: fixed !important;
          top: -24px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          border: 12px solid transparent !important;
          border-top-color: var(--brand) !important;
          z-index: 1000000 !important;
          pointer-events: none !important;
        }
        
        /* Special red glow for Assign Staff button */
        .assign-staff-btn.joyride-glow {
          border-color: #ef4444 !important;
        }
        
        .assign-staff-btn.joyride-glow[data-guide-label]::after {
          background: #ef4444 !important;
        }
        
        .assign-staff-btn.joyride-glow[data-guide-label]::before {
          border-top-color: #ef4444 !important;
        }
      `}</style>
    </>
  );
}